import classNames from 'classnames';
import * as React from 'react';

import { DirectFormInput } from '../../../components/DirectFormInput/DirectFormInput';
import { DropdownChooser, DropdownChooserView } from '../../../components/DropdownChooser/DropdownChooser';
import { ErrorText, ErrorTextTheme } from '../../../components/ErrorText/ErrorText';
import { HoverTooltipWrapper } from '../../../components/HoverTooltipWrapper/HoverTooltipWrapper';
import { InputFieldType } from '../../../components/InputField/InputField';
import { Shape2, Shape2Icon, Shape2Theme } from '../../../components/Shape2/Shape2';
import { TooltipDecoratorProps } from '../../../components/TooltipDecorator/ScopedTooltip';
import { TooltipWrapper } from '../../../components/TooltipWrapper/TooltipWrapper';
import { Bem } from '../../../service/Bem/Bem';
import { ValidationErrorData } from '../../../typings/common';
import { FileEditorContainer } from '../../FileEditor/FileEditorContainer';
import { ColorEditor } from '../ColorEditor/ColorEditor';
import { CreativeDisclaimerOption } from '../CreativeDisclaimerOption/CreativeDisclaimerOption';
import { CreativePositionOption } from '../CreativePositionOption/CreativePositionOption';
import { getAgeItems, getPositionValues, getSelectItems } from '../helpers';
import { CommonOptionName, CreativeOptionData, CreativeOptionType, CreativeOptionValue } from '../typings';

import './CreativeOption.css';

export interface CreativeOptionProps {
    cls?: string;
    data: CreativeOptionData;
    value: CreativeOptionValue;
    placeholder?: string;
    errors?: ValidationErrorData[];
    change: (optionName: string, value: string) => void;
    tooltipsVisible?: boolean;
    groupName: string;
    // пропса для параметра дисклеймер
    countryId?: string | null;
    isDocCenterTooltip?: boolean;
}

const BLOCK = 'creative-option';
const DROPDOWN_SEARCH_FILTER = { isEnabled: false };
const ERROR_TOOLTIP_PROPS: Partial<TooltipDecoratorProps> = {
    view: 'classic',
    tone: 'default',
    size: 'n',
    to: 'left',
    popupProps: {
        hiding: true
    }
};

export class CreativeOption extends React.PureComponent<CreativeOptionProps> {
    render(): React.ReactNode {
        const { visible, title, type } = this.props.data;
        const { cls } = this.props;

        if (visible === false) {
            return null;
        }

        const titleVisible = title !== '' && type !== CreativeOptionType.Text && type !== CreativeOptionType.Disclaimer;

        return (
            <div className={classNames(BLOCK, cls)}>
                {type !== CreativeOptionType.Text && this.renderErrorTooltip()}
                {titleVisible && (
                    <div className={Bem.cls(BLOCK, 'title')}>
                        {title}
                        {this.renderTooltip()}
                    </div>
                )}
                <div
                    className={Bem.cls(BLOCK, 'editor', {
                        text: type === CreativeOptionType.Text
                    })}
                >
                    {this.renderEditor()}
                </div>
            </div>
        );
    }

    private renderEditor(): React.ReactNode {
        const { errors, placeholder, data, groupName, value, tooltipsVisible } = this.props;
        const editable = data.editable === undefined ? true : data.editable;

        const textValue = value || '';

        if (!editable && (value === undefined || value === '')) {
            return null;
        }

        switch (data.type) {
            case CreativeOptionType.Text:
                const { limit } = data;

                return (
                    <DirectFormInput
                        theme={'normal'}
                        size={'s'}
                        text={textValue}
                        inputType={InputFieldType.TEXT}
                        onChange={this.onChange}
                        errorText={<ErrorText theme={ErrorTextTheme.tooltip} errors={errors} />}
                        valid={!errors || errors.length === 0}
                        count={editable && limit ? limit - textValue.length : undefined}
                        placeholder={placeholder}
                        disabled={!editable}
                        errorTooltip={ERROR_TOOLTIP_PROPS}
                        keepFocus={true}
                    />
                );

            case CreativeOptionType.Age:
                return (
                    <DropdownChooser
                        cls={Bem.cls(BLOCK, 'age-input')}
                        isMulti={false}
                        size={'xs' as 's'}
                        view={DropdownChooserView.classic}
                        labelButton={`+${textValue}`}
                        value={textValue}
                        onChange={this.onChange}
                        searchFilter={DROPDOWN_SEARCH_FILTER}
                        disabled={!editable}
                        menuItems={getAgeItems(data.allowedValues)}
                        width={90}
                    />
                );

            case CreativeOptionType.ColorPicker:
                return (
                    <ColorEditor
                        color={textValue}
                        colorChange={this.onChange}
                        allowedColors={data.allowedValues}
                        disabled={!data.editable}
                        visible={tooltipsVisible}
                    />
                );

            case CreativeOptionType.Select:
                return (
                    <DropdownChooser
                        cls={Bem.cls(BLOCK, 'select-input')}
                        isMulti={false}
                        size={'xs' as 's'}
                        view={DropdownChooserView.classic}
                        labelButton={textValue}
                        value={textValue}
                        onChange={this.onChange}
                        searchFilter={DROPDOWN_SEARCH_FILTER}
                        disabled={!editable}
                        menuItems={getSelectItems(data.allowedValues)}
                        width={160}
                    />
                );

            case CreativeOptionType.Position:
                return (
                    <CreativePositionOption
                        value={textValue}
                        allowedValues={getPositionValues(data.allowedValues)}
                        click={this.onChange}
                    />
                );

            case CreativeOptionType.Disclaimer:
                const { items, groups } = data;

                return (
                    <CreativeDisclaimerOption
                        id={textValue}
                        countryId={this.props.countryId}
                        items={items}
                        groups={groups}
                        change={this.onDisclaimerChange}
                        countryChange={this.onDisclaimerCountryChange}
                    />
                );

            case CreativeOptionType.File:
                const { sources, editorId, resourceType, name } = data;

                return (
                    <FileEditorContainer
                        resourceType={resourceType}
                        fileIdValue={value}
                        disabled={!editable}
                        sources={sources}
                        onValueChange={this.onChange}
                        editorId={editorId}
                        name={name}
                        groupName={groupName}
                    />
                );

            default:
                return null;
        }
    }

    private renderTooltip(): React.ReactNode {
        const { tooltip } = this.props.data;
        const { tooltipsVisible, isDocCenterTooltip } = this.props;

        if (!tooltip) {
            return null;
        }

        return (
            <HoverTooltipWrapper
                size="xs"
                to={'bottom'}
                tooltipText={tooltip}
                isDoccenterTooltip={isDocCenterTooltip}
                externalVisible={tooltipsVisible}
            >
                <Shape2
                    icon={Shape2Icon.help}
                    theme={Shape2Theme.default}
                    width={11}
                    height={11}
                    cls={Bem.cls(BLOCK, 'icon-info')}
                />
            </HoverTooltipWrapper>
        );
    }

    private renderErrorTooltip(): React.ReactNode {
        const { errors, tooltipsVisible } = this.props;

        if (!errors || errors.length === 0) {
            return null;
        }

        return (
            <TooltipWrapper
                {...ERROR_TOOLTIP_PROPS}
                cls={Bem.cls(BLOCK, 'tooltip')}
                anchor={this}
                theme="error"
                visible={tooltipsVisible}
                autoclosable={false}
                tooltipContent={
                    <div className={Bem.cls(BLOCK, 'error-tooltip-content')}>
                        <ErrorText theme={ErrorTextTheme.tooltip} errors={errors} />
                    </div>
                }
            />
        );
    }

    private onChange = (value: string): void => {
        const { name } = this.props.data;

        this.props.change(name, value);
    };

    private onDisclaimerCountryChange = (value: string): void => {
        this.props.change(CommonOptionName.disclaimerCountry, value);
    };

    private onDisclaimerChange = (value: string): void => {
        this.props.change(CommonOptionName.disclaimer, value);
    };
}
